import React from "react";
import { Box, Breadcrumbs, Typography } from "@mui/material";

interface PageHeaderProps {
  /**
   * One or more `PageHeader.Breadcrumb` nodes that form the breadcrumb trail.
   * They are rendered inside a MUI `Breadcrumbs` container separated by →.
   */
  children?: React.ReactNode;
  /** Page title rendered as an h1. */
  title: string;
}

/**
 * Reusable page header that renders an optional breadcrumb trail and an h1
 * title, styled to match the legacy `.breadcrumb` / `.content-header-title`
 * CSS rules from `App.css` and `index.css`.
 *
 * @example
 * <PageHeader title="Manage Locations">
 *   <BreadCrumb>Account</BreadCrumb>
 *   <BreadCrumb>Manage Locations</BreadCrumb>
 * </PageHeader>
 *
 * @see {@link Breadcrumb} for the individual breadcrumb items that go inside the `PageHeader`.
 */
const PageHeader = ({ children, title }: PageHeaderProps) => {
  return (
    <Box component="header">
      {children && (
        <Breadcrumbs
          separator="→"
          aria-label="breadcrumb"
          sx={{
            padding: "20px 0 7px 0",
            marginBottom: 0,
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
              alignItems: "center",
            },
            "& .MuiBreadcrumbs-separator": {
              color: "#5C6771",
              fontSize: "12px",
            },
          }}
        >
          {children}
        </Breadcrumbs>
      )}

      <Typography
        component="h1"
        sx={{
          fontFamily: "Montserrat, sans-serif",
          paddingTop: 0,
          paddingBottom: "13px",
          color: "#324559",
          fontSize: { xs: "30px", sm: "32px" },
          fontWeight: "bold",
          lineHeight: "38px",
          textAlign: "left",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

export default PageHeader;
